import { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons"
import { Field, Form, Formik } from "formik"
import { useNavigate } from "react-router-dom"
import { useGenerateQRCode, useVerifyOTP } from "../../hooks/useTwoFa"
import { useLogout } from "../../hooks/useAuth"
import routePaths from "../../constants/path"
import { verifyOtpSchema } from "./schema"
import TextField from "../../components/TextField"
import ApiError from "../../components/ApiError"

const EnableTwoFa = () => {
  const navigate = useNavigate()
  const [currentStep, setCurrentStep] = useState(0)
  const {
    isPending,
    mutateAsync: generateQRCode,
    data: qrCodeResponse,
  } = useGenerateQRCode()
  const {
    isPending: verifyingOTP,
    mutate: verifyOTP,
    error: verifyOTPError,
    isError: isVerifyOTPError,
  } = useVerifyOTP()
  const { mutate: logout } = useLogout()

  const handleNext = () => setCurrentStep((prev) => prev + 1)
  const handleBack = () => setCurrentStep((prev) => prev - 1)

  const handleGenerateQRCode = async () => {
    await generateQRCode()
    handleNext()
  }

  const handleVerifyOTP = (values) => {
    verifyOTP({ otp: values.otp, rememberMe: !!values.rememberMe })
  }

  const handleCancel = () => {
    logout(undefined, {
      onSettled: () => {
        navigate(routePaths.login, { replace: true })
      },
    })
  }

  const steps = [
    {
      title: "Secure Your Account with Two-Factor Authentication",
      content: (
        <div className="max-h-max p-4 flex flex-col gap-1">
          <p className="text-lg">
            To enhance the security of your account, please enable two-factor
            authentication (2FA).
          </p>
        </div>
      ),
      action: (
        <div className="flex justify-center mt-2">
          <button
            className="bg-secondary hover:bg-secondary_dark text-white px-6 py-2 rounded-lg"
            onClick={handleNext}
          >
            Next
          </button>
        </div>
      ),
    },
    {
      title: "Download an Authenticator App",
      content: (
        <div className="max-h-max p-4 flex flex-col gap-1">
          <p className="text-lg">
            To get started, download an authenticator app on your smartphone if
            you haven&apos;t already.
          </p>
          <strong className="mt-2 text-lg">Recommended Apps:</strong>
          <ul className="flex flex-col gap-1 ml-1 mt-1">
            <li>
              <a
                className="text-secondary underline"
                href="https://apps.apple.com/us/app/google-authenticator/id388497605"
                rel="noreferrer"
                target="_blank"
              >
                Google Authenticator for iOS
              </a>
            </li>
            <li>
              <a
                className="text-secondary underline"
                href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2&pcampaignid=web_share"
                rel="noreferrer"
                target="_blank"
              >
                Google Authenticator for Android
              </a>
            </li>
            <li>
              <a
                className="text-secondary underline"
                href="https://apps.apple.com/us/app/twilio-authy/id494168017"
                rel="noreferrer"
                target="_blank"
              >
                Authy for iOS
              </a>
            </li>
            <li>
              <a
                className="text-secondary underline"
                href="https://play.google.com/store/apps/details?id=com.authy.authy&pcampaignid=web_share"
                rel="noreferrer"
                target="_blank"
              >
                Authy for Android
              </a>
            </li>
          </ul>
        </div>
      ),
      action: (
        <div className="flex justify-center mt-2">
          <button
            className="bg-secondary hover:bg-secondary_dark text-white px-6 py-2 rounded-lg disabled:text-gray-300"
            disabled={isPending}
            onClick={handleGenerateQRCode}
          >
            Next
          </button>
        </div>
      ),
    },
    {
      title: "Scan the QR Code",
      content: (
        <div className="max-h-max p-4 flex flex-col gap-1">
          <p className="text-lg">
            Open the authenticator app and scan the QR code below. This will add
            your account to the app and generate a unique code.
          </p>
          <div className="mt-2 flex justify-center">
            <img
              alt="Qr-code"
              className="w-96 h-96"
              src={qrCodeResponse?.qrCode}
            />
          </div>
        </div>
      ),
      action: (
        <div className="flex justify-center mt-2">
          <button
            className="bg-secondary hover:bg-secondary_dark text-white px-6 py-2 rounded-lg"
            onClick={handleNext}
          >
            I have scanned the QR Code
          </button>
        </div>
      ),
    },
    {
      title: "Enter the Verification Code",
      content: (
        <div className="max-h-max p-4 flex flex-col gap-1">
          <p className="text-lg">
            After scanning the QR code, your authenticator app will display a
            6-digit code. Enter this code below to verify and complete the
            setup.
          </p>
          <div className="mt-2">
            <Formik
              enableReinitialize
              initialValues={{ otp: "", rememberMe: true }}
              validationSchema={verifyOtpSchema}
              onSubmit={handleVerifyOTP}
            >
              <Form className="mt-2 flex flex-col gap-6">
                <TextField
                  label="Authentication Code"
                  name="otp"
                  placeholder="Enter 6-digit code"
                />
                <div className="flex ml-2 gap-2">
                  <Field id="rememberMe" name="rememberMe" type="checkbox" />
                  <label className="text-sm" htmlFor="rememberMe">
                    Remember this device
                  </label>
                </div>
                {isVerifyOTPError && <ApiError axiosError={verifyOTPError} />}

                <button
                  className="w-full bg-secondary hover:bg-secondary_dark text-white px-6 py-2 rounded-lg disabled:bg-gray-400"
                  disabled={verifyingOTP}
                  type="submit"
                >
                  Verify
                </button>
              </Form>
            </Formik>
          </div>
        </div>
      ),
      action: null,
    },
  ]

  return (
    <div className="flex items-center justify-center mt-4">
      <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full overflow-hidden pb-4">
        <div className="pt-3 pb-3 px-4 flex items-center gap-4">
          {currentStep !== 0 && (
            <FontAwesomeIcon
              className="cursor-pointer"
              icon={faChevronLeft}
              onClick={handleBack}
            />
          )}
          <p className="text-xl">
            <strong>{steps[currentStep].title}</strong>
          </p>
        </div>
        {steps[currentStep].content}
        {steps[currentStep].action}
        <div className="mt-4 flex justify-center">
          <button
            className="text-secondary underline text-sm"
            type="button"
            onClick={handleCancel}
          >
            Cancel and log out
          </button>
        </div>
      </div>
    </div>
  )
}

export default EnableTwoFa
